import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CG_BOOST_PLATFORMS,
  CgBoostCampaign,
  CgBoostPullStatus,
  getPullStatus,
  listCampaigns,
} from '../../services/cgBoostApi';
import { usePermissions } from '../../hooks/usePermissions';
import NewCampaignModal from './NewCampaignModal';
import SnapshotView from './views/SnapshotView';
import PlatformsView from './views/PlatformsView';
import CreativeView from './views/CreativeView';
import MomentumView from './views/MomentumView';
import SignalsView from './views/SignalsView';
import IntelligenceView from './views/IntelligenceView';
import LinkStatusPill from './LinkStatusPill';
import PlatformHealthBadge from './PlatformHealthBadge';
import PullDiagnosticsDrawer from './PullDiagnosticsDrawer';
import HistoryDrawer from './HistoryDrawer';
import { Button, Tabs, StatusPill, Pill, DashboardFrame } from '../ui';

type ViewKey = 'snapshot' | 'platforms' | 'creative' | 'momentum' | 'signals' | 'intelligence';

const VIEWS: Array<{ value: ViewKey; label: string }> = [
  { value: 'snapshot',     label: 'Snapshot' },
  { value: 'platforms',    label: 'Platforms' },
  { value: 'creative',     label: 'Creative' },
  { value: 'momentum',     label: 'Momentum' },
  { value: 'signals',      label: 'Signals' },
  { value: 'intelligence', label: 'Intelligence' },
];

const statusVariant = (status: string): 'live' | 'pending' | 'paused' | 'draft' => {
  const s = status.toLowerCase();
  if (s.includes('active') || s.includes('live')) return 'live';
  if (s.includes('pending')) return 'pending';
  if (s.includes('pause')) return 'paused';
  return 'draft';
};

const CgBoostDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { has, loading: permsLoading } = usePermissions();

  const [campaigns, setCampaigns] = useState<CgBoostCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [pullStatuses, setPullStatuses] = useState<CgBoostPullStatus[]>([]);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const selectedCampaignId = searchParams.get('campaign');
  const activeView = (searchParams.get('view') as ViewKey) || 'snapshot';

  const allowed = has('cgBoost.view');

  useEffect(() => {
    if (permsLoading || !allowed) return;
    let cancelled = false;
    (async () => {
      setCampaignsLoading(true);
      try {
        const list = await listCampaigns();
        if (cancelled) return;
        setCampaigns(list);
        if (!selectedCampaignId && list.length > 0) {
          const preferred = list.find((c) => c.status === 'active') ?? list[0];
          setSearchParams({ campaign: preferred.canonicalCampaignId, view: activeView });
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load campaigns');
      } finally {
        if (!cancelled) setCampaignsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsLoading, allowed]);

  const currentCampaign = useMemo(
    () => campaigns.find((c) => c.canonicalCampaignId === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId],
  );

  // Pull-status load: fires on campaign switch + after retries. Failures are
  // soft — the dashboard still renders without badges if the endpoint errors.
  const refetchPullStatus = React.useCallback(async () => {
    if (!selectedCampaignId) return;
    try {
      const statuses = await getPullStatus(selectedCampaignId);
      setPullStatuses(statuses);
    } catch (err) {
      console.warn('[CgBoostDashboard] pull-status fetch failed:', err);
      setPullStatuses([]);
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignId) return;
    let cancelled = false;
    (async () => {
      const statuses = await getPullStatus(selectedCampaignId).catch(() => [] as CgBoostPullStatus[]);
      if (!cancelled) setPullStatuses(statuses);
    })();
    return () => { cancelled = true; };
  }, [selectedCampaignId]);

  const selectCampaign = (id: string) => {
    setSearchParams({ campaign: id, view: activeView });
  };

  const selectView = (view: ViewKey) => {
    if (selectedCampaignId) {
      setSearchParams({ campaign: selectedCampaignId, view });
    } else {
      setSearchParams({ view });
    }
  };

  if (permsLoading) {
    return <CenteredMessage text="Verifying access…" />;
  }
  if (!allowed) {
    return (
      <CenteredMessage
        text="Access denied. CG Boost requires the cgBoost.view permission."
        actionLabel="Back to Chat"
        onAction={() => navigate('/chat')}
      />
    );
  }

  const renderView = () => {
    if (!selectedCampaignId) {
      return <CenteredMessage text="No campaigns yet. Create one to get started." />;
    }
    switch (activeView) {
      case 'snapshot':     return <SnapshotView campaignId={selectedCampaignId} />;
      case 'platforms':    return <PlatformsView campaignId={selectedCampaignId} />;
      case 'creative':     return <CreativeView campaignId={selectedCampaignId} />;
      case 'momentum':     return <MomentumView campaignId={selectedCampaignId} />;
      case 'signals':      return <SignalsView campaignId={selectedCampaignId} />;
      case 'intelligence': return <IntelligenceView campaignId={selectedCampaignId} />;
      default:             return null;
    }
  };

  const banners = (error || successBanner) ? (
    <>
      {error && <div style={errorBanner}>{error}</div>}
      {successBanner && <div style={successBannerStyle}>{successBanner}</div>}
    </>
  ) : null;

  return (
    <DashboardFrame
      eyebrow="CG Boost · Campaign performance"
      title={currentCampaign ? currentCampaign.brandName : 'CG Boost'}
      topBarLeftExtras={<Pill tone="warning" size="sm">Placeholder data</Pill>}
      topBarRight={
        <Button variant="primary" size="md" onClick={() => setShowNewModal(true)}>
          + New campaign
        </Button>
      }
      controlRow={
        <div style={campaignSelector}>
          <span style={selectorLabel}>Campaign</span>
          {campaignsLoading ? (
            <span style={selectorMuted}>Loading…</span>
          ) : campaigns.length === 0 ? (
            <span style={selectorMuted}>No campaigns yet</span>
          ) : (
            <select
              value={selectedCampaignId ?? ''}
              onChange={(e) => selectCampaign(e.target.value)}
              style={selectControl}
            >
              {campaigns.map((c) => (
                <option key={c.canonicalCampaignId} value={c.canonicalCampaignId}>
                  {c.brandName} — {c.campaignName ?? c.canonicalCampaignId} ({c.status})
                </option>
              ))}
            </select>
          )}
        </div>
      }
      tabs={
        <Tabs<ViewKey>
          value={activeView}
          onChange={selectView}
          variant="underlined"
          options={VIEWS}
        />
      }
      statusBanner={banners}
    >
      {currentCampaign && (
        <div style={campaignHeaderCard}>
          <div style={{ minWidth: 0 }}>
            <div style={campaignHeaderTitle}>
              {currentCampaign.brandName} — {currentCampaign.campaignName ?? 'Untitled'}
            </div>
            <div style={campaignHeaderMeta}>
              {currentCampaign.industryVertical} · {currentCampaign.startDate} → {currentCampaign.endDate}
              {' · '}
              <StatusPill variant={statusVariant(currentCampaign.status)} size="sm" style={{ verticalAlign: 'middle' }}>
                {currentCampaign.status}
              </StatusPill>
              {' · '}
              <span style={{ verticalAlign: 'middle' }}>
                <LinkStatusPill status={currentCampaign.linkStatus} short />
              </span>
            </div>
            {pullStatuses.length > 0 && (
              <div style={healthRow}>
                {CG_BOOST_PLATFORMS.map((platform) => {
                  const status = pullStatuses.find((s) => s.platform === platform);
                  if (!status) return null;
                  return (
                    <PlatformHealthBadge
                      key={platform}
                      platform={platform}
                      status={status}
                      onClick={() => setShowDiagnostics(true)}
                    />
                  );
                })}
              </div>
            )}
          </div>
          <div style={creatorRow}>
            {currentCampaign.creators.map((c) => (
              <Pill key={c.slug} tone="accent" size="sm">{c.name}</Pill>
            ))}
            <Pill tone="neutral" size="sm" onClick={() => setShowHistory(true)}>
              History
            </Pill>
          </div>
        </div>
      )}
      {renderView()}

      {currentCampaign && (
        <HistoryDrawer
          open={showHistory}
          onClose={() => setShowHistory(false)}
          canonicalCampaignId={currentCampaign.canonicalCampaignId}
          campaignTitle={`${currentCampaign.brandName} — ${currentCampaign.campaignName ?? 'Untitled'}`}
        />
      )}

      {currentCampaign && (
        <PullDiagnosticsDrawer
          open={showDiagnostics}
          onClose={() => setShowDiagnostics(false)}
          campaign={currentCampaign}
          statuses={pullStatuses}
          onRetried={() => {
            // Wait a beat so the pipeline has a chance to write status.
            setTimeout(refetchPullStatus, 1500);
          }}
        />
      )}

      {showNewModal && (
        <NewCampaignModal
          onClose={() => setShowNewModal(false)}
          onCreated={(campaign) => {
            setShowNewModal(false);
            setCampaigns((prev) => {
              const filtered = prev.filter(
                (c) => c.canonicalCampaignId !== campaign.canonicalCampaignId,
              );
              return [campaign, ...filtered];
            });
            setSearchParams({ campaign: campaign.canonicalCampaignId, view: activeView });
            setSuccessBanner(
              `Created ${campaign.brandName} (${campaign.canonicalCampaignId}). ` +
                'Daily metrics use placeholder data until TikTok / Meta / YouTube keys land.',
            );
          }}
        />
      )}
    </DashboardFrame>
  );
};

const CenteredMessage: React.FC<{ text: string; actionLabel?: string; onAction?: () => void }> = ({ text, actionLabel, onAction }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 16 }}>
    <p style={{ color: 'var(--fg-3)', fontSize: 14 }}>{text}</p>
    {actionLabel && onAction && (
      <Button variant="primary" size="md" onClick={onAction}>{actionLabel}</Button>
    )}
  </div>
);

const campaignSelector: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '14px 32px',
  borderBottom: '1px solid var(--border-subtle)',
};

const selectorLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--fg-4)',
  textTransform: 'uppercase', letterSpacing: '0.08em',
};

const selectorMuted: React.CSSProperties = { fontSize: 13, color: 'var(--fg-3)' };

const selectControl: React.CSSProperties = {
  flex: 1, maxWidth: 560,
  padding: '8px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-2)',
  fontSize: 13,
  color: 'var(--fg-1)',
  background: '#fff',
  fontFamily: 'var(--font-sans)',
  boxShadow: 'var(--shadow-inset)',
};

const campaignHeaderCard: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '18px 20px',
  borderRadius: 'var(--radius-lg)',
  background: 'linear-gradient(135deg, rgba(14,31,58,0.08), rgba(14,31,58,0.01))',
  border: '1px solid var(--border-1)',
  gap: 16,
  flexWrap: 'wrap',
};

const campaignHeaderTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--fg-1)',
  fontFamily: 'var(--font-display)',
  letterSpacing: '-0.01em',
};

const campaignHeaderMeta: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  marginTop: 4,
};

const creatorRow: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
};

const healthRow: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
  marginTop: 8,
};

const errorBanner: React.CSSProperties = {
  margin: '16px 32px',
  padding: '12px 14px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
  border: '1px solid var(--cg-terra-200)',
  fontSize: 13,
};

const successBannerStyle: React.CSSProperties = {
  margin: '16px 32px',
  padding: '12px 14px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--accent-2-soft)',
  color: 'var(--accent-2-soft-fg)',
  border: '1px solid var(--cg-saffron-200)',
  fontSize: 13,
};

export default CgBoostDashboard;
